import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import ProviderManager from '../../../provider/providerManager';
import {validateFields, validateField} from '../../../utilities/validation/validator';
import {stepEnum, bitPaymentStatusEnum, paymentType, paymentAction, popupEnum} from './Constants/debtPaymentEnum';
import lodash from 'lodash';
import {bitStatusValidation, createBitRequest, getCurrentPaymentStatus} from 'app/Components/Shared/BitPayment/bitPaymentSlice';
import {hideAppLoader, showAppLoader} from 'app/Components/Shared/Loader/LoaderSlice';
import AuthenticationProvider from '../../../utilities/authentication/authenticationProvider';
import {processAndGroupBans, mapBackendToClientModel} from './utils/banTypeClassifier';
import {BanTypeDisplayNames} from './Constants/banTypeEnum';
import {resetStore as resetCGStore} from '../SaleBlock/PaymentCG/PaymentCGSlice';
import {bankValidationErrorMessage} from './Constants/banPaymentDataTypeEnum';

const getListFromResponse = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.Body)) {
    return payload.Body;
  }
  return [];
};

export const creditCardPay = createAsyncThunk('debtAndUpdatePayment/creditCardPay', async (data, thunkAPI) => {
  try {
    thunkAPI.dispatch(showAppLoader());
    const result = await ProviderManager.getProviderData('debtPayByCreditCard', data);

    if (result.Header?.ReturnCode === 0) {
      return {
        returnCode: result.Body.returnCode,
        success: result.Body.success,
        receptionNo: result.Body.reception,
        tvNotification: result.Body.tvNotification,
        returnMessage: result.Body.returnMessage,
        invoiceByEmail: result.Body.invoiceByEmail,
        updatePaymentSuccess: result.Body.updatePaymentSuccess,
        customerEmail: result.Body.customerEmail,
        hasCellular: result.Body.hasCellular,
        hasInternet: result.Body.hasInternet,
      };
    } else {
      return {
        returnCode: stepEnum.PAYMENTFAILED,
        success: result.Body.Success,
        receptionNo: '',
      };
    }
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  } finally {
    thunkAPI.dispatch(resetCGStore());
    thunkAPI.dispatch(hideAppLoader());
  }
});

export const getDebts = createAsyncThunk('debtAndUpdatePayment/getDebts', async (data, thunkAPI) => {
  try {
    thunkAPI.dispatch(showAppLoader());
    const result = await ProviderManager.getProviderData('getCustomerPaymentsData', []);

    const bans = result.Body?.bans;

    if (data.bitEvent) {
      const bitEvent = data.bitEvent;
      const selectedAmount = data.selectedAmount;
      const selectedBan = data.selectedBan;
      const externalSystemReference = data.externalSystemReference;

      return {
        bans,
        bitEvent,
        selectedAmount,
        selectedBan,
        externalSystemReference,
      };
    }
    return {bans};
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  } finally {
    thunkAPI.dispatch(hideAppLoader());
  }
});

export const getBanksList = createAsyncThunk('debtAndUpdatePayment/getBanksList', async (_, thunkAPI) => {
  try {
    thunkAPI.dispatch(showAppLoader());
    return await ProviderManager.getProviderData('getBanksList', []);
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  } finally {
    thunkAPI.dispatch(hideAppLoader());
  }
});

export const getBankBranchesList = createAsyncThunk('debtAndUpdatePayment/getBankBranchesList', async (data, thunkAPI) => {
  try {
    return await ProviderManager.getProviderData('getBankBranchesList', [data.bankCode]);
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const paymentMethodValidation = createAsyncThunk('debtAndUpdatePayment/paymentMethodValidation', async (data, thunkAPI) => {
  try {
    thunkAPI.dispatch(showAppLoader());
    const result = await ProviderManager.getProviderData('PaymentMethodValidation', data);

    if (result?.Body?.isSuccess) {
      thunkAPI.dispatch({type: 'debtAndUpdatePayment/saveToHistory', payload: stepEnum.BANKRANCHSELECTION});
    }

    return result;
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  } finally {
    thunkAPI.dispatch(hideAppLoader());
  }
});

export const openBitRequest = createAsyncThunk('debtAndUpdatePayment/openBitRequest', async (data, thunkAPI) => {
  try {
    thunkAPI.dispatch(showAppLoader());
    const res = await thunkAPI.dispatch(createBitRequest(data));
    return res.payload;
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  } finally {
    thunkAPI.dispatch(hideAppLoader());
  }
});

export const getBitPaymentStatus = createAsyncThunk('debtAndUpdatePayment/getBitPaymentStatus', async (data, thunkAPI) => {
  try {
    thunkAPI.dispatch(showAppLoader());
    const res = await thunkAPI.dispatch(bitStatusValidation(data));
    return res.payload;
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  } finally {
    thunkAPI.dispatch(hideAppLoader());
  }
});

export const getBitPaymentStatusOnLoading = createAsyncThunk(
  'debtAndUpdatePayment/getBitPaymentStatusOnLoading',
  async (data, thunkAPI) => {
    try {
      const res = await thunkAPI.dispatch(getCurrentPaymentStatus(data));
      return res.payload;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const getBankObligationSignatureFile = createAsyncThunk(
  'debtAndUpdatePayment/getBankObligationSignatureFile',
  async (data, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const debtState = state.debtAndUpdatePayment;
      const userDetails = state.authentication?.loginData?.userDetails ?? {};
      const request = data ?? {
        RequestType: 0,
        BanNumber: debtState.selectedBanData?.banNumber,
        BankCode: debtState.bankObligationInfo?.bankCode,
        BranchCode: debtState.bankObligationInfo?.branchCode,
        BankDesc: debtState.bankObligationInfo?.bankName,
        BankAccountNo: debtState.bankObligationInfo?.bankAccountNo,
      };

      const result = await ProviderManager.getProviderData('getBankObligationSignatureFile', request);
      return result.Body;
    } catch (error) {
      return error;
    }
  },
);

export const checkBankObligationSignatureFile = createAsyncThunk(
  'debtAndUpdatePayment/checkBankObligationSignatureFile',
  async (data, thunkAPI) => {
    thunkAPI.dispatch(showAppLoader());
    try {
      const state = thunkAPI.getState();
      const debtState = state.debtAndUpdatePayment;

      const request = data ?? {
        RequestType: 1,
        FileGuid: debtState.signatureFile.guid,
        BanNumber: debtState.selectedBanData?.banNumber,
      };

      const result = await ProviderManager.getProviderData('getBankObligationSignatureFile', request);
      thunkAPI.dispatch(hideAppLoader());
      return result.Body;
    } catch (error) {
      thunkAPI.dispatch(hideAppLoader());
      return error;
    }
  },
);

export const submitCCPaymentMethod = createAsyncThunk('debtAndUpdatePayment/submitCCPaymentMethod', async (data, thunkAPI) => {
  try {
    thunkAPI.dispatch(showAppLoader());
    const result = await ProviderManager.getProviderData('submitCCPaymentMethod', data);
    return result.Body;
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  } finally {
    thunkAPI.dispatch(resetCGStore());
    thunkAPI.dispatch(hideAppLoader());
  }
});

export const submitBankPaymentMethod = createAsyncThunk('debtAndUpdatePayment/submitBankPaymentMethod', async (data, thunkAPI) => {
  try {
    thunkAPI.dispatch(showAppLoader());
    const result = await ProviderManager.getProviderData('submitBankPaymentMethod', data);

    return result.Body;
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  } finally {
    thunkAPI.dispatch(hideAppLoader());
  }
});

export const sendEntityPaymentML = createAsyncThunk('debtAndUpdatePayment/sendEntityPaymentML', async (data, thunkAPI) => {
  try {
    thunkAPI.dispatch(showAppLoader());
    const result = await ProviderManager.getProviderData('onlyMobileML', data);
    return result;
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  } finally {
    thunkAPI.dispatch(resetCGStore());
    thunkAPI.dispatch(hideAppLoader());
  }
});

function applyBitPaymentSuccess(state, payload) {
  state.paymentData.receptionNo = payload.receptionNo;
  state.paymentData.Amount = payload.paymentAmount;
  state.paymentData.PaymentNo = 1;
  state.paymentData.CCFourLastNumbers = payload.credit4Digits;
  state.paymentData.updatePaymentSuccess = false;
  state.selectedPaymentType = paymentType.BIT;
  state.paymentData.invoiceByEmail = true;
  state.paymentData.customerEmail = payload.customerEmail;
  state.paymentData.PhoneNumber = payload.phoneNumber;
}

export function getBitResultStep(status, statusCode) {
  let nextStep;
  status = status.toLowerCase();
  const errorStatuses = [2, 3, 7, 17];

  switch (status) {
    case bitPaymentStatusEnum.PAID:
      nextStep = stepEnum.PAYMENTSUCCESS;
      break;
    case bitPaymentStatusEnum.DONEWITHFU:
    case bitPaymentStatusEnum.BITPENDING:
      nextStep = stepEnum.BITPENDING;
      break;
    case bitPaymentStatusEnum.DONEWITHERROR:
      if (statusCode == '15' || statusCode == '5') {
        nextStep = stepEnum.BITPAYMENTIMPOSSIBLE;
      } else if (errorStatuses.includes(parseInt(statusCode))) {
        nextStep = stepEnum.BITPAYMENTFAILED;
      } else {
        nextStep = stepEnum.BITGENERALERROR;
      }
      break;
    case bitPaymentStatusEnum.FAILEDTOCANCEL:
      nextStep = stepEnum.BITPAYMENTIMPOSSIBLE;
      break;
    case bitPaymentStatusEnum.CANCELLED:
      nextStep = stepEnum.BITPAYMENTFAILED;
      break;
  }
  return nextStep;
}

/**
 * Processes the bansList from backend:
 * 1. Maps backend fields to client model
 * 2. Classifies each Ban by type (1-7)
 * 3. Groups Bans (Group A: Financial Issues, Group B: No Financial Issues)
 * 4. Sorts within groups by type priority and billing date
 * @returns {Object} Grouped bans with displayConfig
 */
export function processBansList(bansList, paymentConfiguration) {
  if (!bansList || bansList.length === 0) {
    return {
      FINANCIAL_ISSUES: [],
      NO_FINANCIAL_ISSUES: [],
      displayConfig: {
        hasBothGroups: false,
        shouldCollapseGroup2: false,
      },
    };
  }

  try {
    // Step 1: Map backend fields to client model
    const mappedBans = bansList.map((ban) => mapBackendToClientModel(ban));

    // Step 2: Process and group Bans by type
    const groupedBans = processAndGroupBans(mappedBans, paymentConfiguration);

    // Step 3: Add display names for debugging/logging (only in development)
    if (process.env.NODE_ENV === 'development') {
      groupedBans.FINANCIAL_ISSUES = groupedBans.FINANCIAL_ISSUES.map((ban) => ({
        ...ban,
        banTypeDisplayName: ban.banType ? BanTypeDisplayNames[ban.banType] : '',
      }));
      groupedBans.NO_FINANCIAL_ISSUES = groupedBans.NO_FINANCIAL_ISSUES.map((ban) => ({
        ...ban,
        banTypeDisplayName: ban.banType ? BanTypeDisplayNames[ban.banType] : '',
      }));
    }

    return groupedBans;
  } catch (error) {
    console.error('Error processing bansList:', error);
    // Return empty groups if processing fails
    return {
      FINANCIAL_ISSUES: [],
      NO_FINANCIAL_ISSUES: [],
      displayConfig: {
        hasBothGroups: false,
        shouldCollapseGroup2: false,
      },
    };
  }
}

const initialState = {
  epiData: {},
  selectedBanData: {},
  groupedBans: {
    FINANCIAL_ISSUES: [],
    NO_FINANCIAL_ISSUES: [],
    displayConfig: {
      hasBothGroups: false,
      shouldCollapseGroup2: false,
    },
  },
  paymentData: {},
  selectedBansForPaymentUpdate: [],
  currentStep: stepEnum.INITIAL,
  paymentAction: paymentAction.PAYMENT,
  showPopUp: false,
  popupType: '',
  popUpProps: {},
  maxPayment: '',
  maxPaymentEnergy: '',
  paymentStatusDisplayDaysX: 0,
  paymentStatusDisplayDaysY: 0,
  energyPaymentStatusDisplayDaysX: 0,
  energyPaymentStatusDisplayDaysY: 0,
  bitPaymentOption: false,
  firstFloorAmount: null,
  secondFloorAmount: null,
  thirdFloorAmount: null,
  firstFloorPayments: null,
  secondFloorPayments: null,
  thirdFloorPayments: null,
  fourthFloorPayments: null,
  paymentAmount: 0,
  isJetConnection: false,
  activeJetBan: '',
  selectedPaymentType: '',
  selectedUpdatePaymentType: '',
  bitReturnEvent: false,
  bitExternalSystemReference: '',
  banksList: [],
  bankObligationInfo: {
    bankCode: '',
    bankName: '',
    branchCode: '',
    bankAccountNo: '',
    isAutomatedBank: false,
  },
  signatureFile: {
    url: '',
    guid: '',
    isSigned: false,
  },
  entityPaymentPhoneNumber: '',
};

const debtAndUpdatePaymentSlice = createSlice({
  name: 'debtAndUpdatePayment',
  initialState: initialState,
  reducers: {
    endProccess: () => initialState,
    logout: () => {
      AuthenticationProvider.clearAuthenticationData();
      return initialState;
    },
    setEpiData: (state, action) => {
      state.epiData = action.payload;
    },
    setPaymentFloors: (state, action) => {
      state.firstFloorAmount = parseFloat(action.payload.firstFloorAmount);
      state.secondFloorAmount = parseFloat(action.payload.secondFloorAmount);
      state.thirdFloorAmount = parseFloat(action.payload.thirdFloorAmount);
      state.firstFloorPayments = parseFloat(action.payload.firstFloorPayments);
      state.secondFloorPayments = parseFloat(action.payload.secondFloorPayments);
      state.thirdFloorPayments = parseFloat(action.payload.thirdFloorPayments);
      state.fourthFloorPayments = parseFloat(action.payload.fourthFloorPayments);
      state.maxPayment = parseFloat(action.payload.maxPayment);
      state.maxPaymentEnergy = parseFloat(action.payload.maxPaymentEnergy);
      state.paymentStatusDisplayDaysX = action.payload.paymentStatusDisplayDaysX ?? state.paymentStatusDisplayDaysX;
      state.paymentStatusDisplayDaysY = action.payload.paymentStatusDisplayDaysY ?? state.paymentStatusDisplayDaysY;
      state.energyPaymentStatusDisplayDaysX = action.payload.energyPaymentStatusDisplayDaysX ?? state.energyPaymentStatusDisplayDaysX;
      state.energyPaymentStatusDisplayDaysY = action.payload.energyPaymentStatusDisplayDaysY ?? state.energyPaymentStatusDisplayDaysY;
    },
    setPaymentAmount: (state, action) => {
      state.paymentAmount = action.payload;
      if (state.bitPaymentOption) {
        state.stepToHandle = stepEnum.SELECTPAYMENTTYPE;
      } else {
        state.stepToHandle = stepEnum.CREDITCARD;
      }
    },
    setPaymentAction: (state, action) => {
      state.paymentAction = action.payload;
    },
    setBitPaymentOption: (state, action) => {
      state.bitPaymentOption = action.payload;
    },
    setIsJetConnection: (state, action) => {
      state.isJetConnection = true;
      state.activeJetBan = action.payload;
    },
    setPaymentData: (state, action) => {
      state.paymentData = action.payload;
    },
    setSendPayment: (state) => {
      state.paymentData.pay = true;
    },
    handleCurrentStep: (state, action) => {
      state.currentStep = action.payload;
    },
    setSelectedPaymentType: (state, action) => {
      state.selectedPaymentType = action.payload;
    },
    setSelectedUpdatePaymentType: (state, action) => {
      state.selectedUpdatePaymentType = action.payload;
    },
    handleBanData: (state, action) => {
      const banData = action.payload;
      state.selectedBanData = banData;
      //state.currentStep = stepEnum.SELECTAMOUNT;
    },
    setSuccessPage: (state, action) => {
      applyBitPaymentSuccess(state, action.payload);
      state.selectedBanData.amount = action.payload.debtAmount;
    },
    setShowPopup: (state, action) => {
      if (action.payload.showPopUp === false) {
        if (state.popupType === popupEnum.DEBT_FIRST && action.payload.mainCloseButton) {
          state.selectedBanData = {};
        }
        state.showPopUp = false;
        state.popupType = '';
        state.popUpProps = {};
      } else {
        state.showPopUp = true;
        state.popupType = action.payload.popupType;
      }
    },
    setSelectedBansForPaymentUpdate: (state, action) => {
      state.selectedBansForPaymentUpdate = action.payload;
    },
    setBankObligationInfo: (state, action) => {
      state.bankObligationInfo = {...state.bankObligationInfo, ...action.payload};
    },
    setSignatureFile: (state, action) => {
      state.signatureFile = {...state.signatureFile, ...action.payload};
    },
    setSignatureFileSigned: (state, action) => {
      state.signatureFile.isSigned = action.payload;
    },
    setEntityPaymentPhoneNumber: (state, action) => {
      state.entityPaymentPhoneNumber = action.payload;
    },
    saveToHistory: (state) => {
      return state;
    },
    backHistory: (state) => {
      return state;
    },
    clearHistory: (state, action) => {
      return state;
    },
  },
  extraReducers: {
    [getDebts.fulfilled]: (state, action) => {
      //state.currentStep = stepEnum.PAYMENTFAILED;
      //return state;
      const bansList = action.payload.bans;
      if (
        (action.payload?.errorCode && action.payload?.errorCode !== '00') ||
        (action.payload.message && action.payload.message.toLowerCase().includes('error'))
      ) {
        state.currentStep = stepEnum.GENERALERROR;
      } else if (bansList && bansList.length === 0) {
        state.currentStep = stepEnum.NOACCOUNTS;
      } else if (action.payload.message && action.payload.message.includes('503')) {
        state.currentStep = stepEnum.GENERALERROR;
      } else {
        // Process bansList: classify types, group, and sort
        const paymentConfiguration = {
          maxPayment: state.maxPayment,
          maxPaymentEnergy: state.maxPaymentEnergy,
          paymentStatusDisplayDaysX: state.paymentStatusDisplayDaysX,
          paymentStatusDisplayDaysY: state.paymentStatusDisplayDaysY,
          energyPaymentStatusDisplayDaysX: state.energyPaymentStatusDisplayDaysX,
          energyPaymentStatusDisplayDaysY: state.energyPaymentStatusDisplayDaysY,
        };
        const groupedBans = processBansList(bansList, paymentConfiguration);

        if (action.payload.bitEvent) {
          state.currentStep = stepEnum.SELECTPAYMENTTYPE;
          state.paymentAmount = action.payload.selectedAmount;
          state.bitExternalSystemReference = action.payload.externalSystemReference;
          state.bitReturnEvent = true;
          // Search for ban in both groups
          const allBans = [...groupedBans.FINANCIAL_ISSUES, ...groupedBans.NO_FINANCIAL_ISSUES];
          const banData = allBans.find((list) => list.banNumber == action.payload.selectedBan);
          if (banData) {
            state.selectedBanData = banData;
            state.groupedBans = groupedBans;
          } else {
            state.currentStep = stepEnum.INCORRECTUSER;
          }
        } else {
          const totalBansCount = groupedBans.FINANCIAL_ISSUES.length + groupedBans.NO_FINANCIAL_ISSUES.length;
          if (totalBansCount === 0) {
            state.currentStep = stepEnum.NOACCOUNTS;
          } else {
            state.groupedBans = groupedBans;
            state.currentStep = stepEnum.BANSELECT;
          }
        }
      }
    },
    [getDebts.rejected]: (state) => {
      state.currentStep = stepEnum.GENERALERROR;
    },
    [getBanksList.fulfilled]: (state, action) => {
      state.banksList = getListFromResponse(action.payload);
    },
    [paymentMethodValidation.fulfilled]: (state, action) => {
      if (action.payload?.Body?.isSuccess) {
        state.currentStep = stepEnum.BANKFORMSIGNATURE;
      } else {
        state.showPopUp = true;
        state.popupType = popupEnum.BANK_ACCOUNT_MISMATCH;
        state.popUpProps = bankValidationErrorMessage[action.payload?.Header?.ReturnCode];
      }
    },
    [paymentMethodValidation.rejected]: (state, action) => {
      state.showPopUp = true;
      state.popupType = popupEnum.BANK_ACCOUNT_MISMATCH;
      const errorCode = action.payload?.Header?.ReturnCode ?? action.payload.errorCode;
      if (bankValidationErrorMessage[errorCode]) {
        state.popUpProps = bankValidationErrorMessage[errorCode];
      } else {
        state.popUpProps = bankValidationErrorMessage['7050'];
      }
    },
    [getBankObligationSignatureFile.pending]: (state, action) => {
      state.signatureFile.loading = true;
    },
    [getBankObligationSignatureFile.fulfilled]: (state, action) => {
      if (!action.payload?.isAxiosError && action.payload?.isSuccess) {
        state.signatureFile.url = action.payload.signUrl;
        state.signatureFile.guid = action.payload.fileGuid;
        state.signatureFile.loading = false;
        state.signatureFile.error = action.payload.fileGuid ? false : true;
        if (action.payload.isSigned) {
          state.signatureFile.isSigned = true;
        }
      }
    },
    [getBankObligationSignatureFile.rejected]: (state) => {
      state.signatureFile.error = true;
      state.signatureFile.loading = false;
    },
    [checkBankObligationSignatureFile.fulfilled]: (state, action) => {
      if (!action.payload?.isAxiosError && action.payload?.isSuccess && action.payload?.isSigned) {
        state.signatureFile.isSigned = true;
        return;
      }

      state.signatureFile = {
        url: '',
        guid: '',
        isSigned: false,
        isLoading: false,
        error: false,
      };
      state.showPopUp = true;
      state.popupType = popupEnum.SIGNATURE_REQUIRED;
    },
    [checkBankObligationSignatureFile.rejected]: (state) => {
      state.signatureFile = {
        url: '',
        guid: '',
        isSigned: false,
        isLoading: false,
      };
      state.showPopUp = true;
      state.popupType = popupEnum.SIGNATURE_REQUIRED;
    },
    [creditCardPay.fulfilled]: (state, action) => {
      if (action.payload.success) {
        state.paymentData = {...state.paymentData, ...action.payload};
        state.currentStep = stepEnum.PAYMENTSUCCESS;
      } else {
        if (action.payload.returnCode == 'ZK205') {
          if (state.selectedBanData.isEnergy) {
            state.currentStep = stepEnum.CREDITPAYMENTERRORENERGY;
          } else {
            state.currentStep = stepEnum.CREDITPAYMENTERROR;
          }
        } else if (!state.bitPaymentOption) {
          state.currentStep = stepEnum.PAYMENTFAILEDWITHOUTBITOPTION;
        } else {
          if (state.selectedBanData.isEnergy) {
            state.currentStep = stepEnum.PAYMENTFAILEDENERGY;
          } else {
            state.currentStep = stepEnum.PAYMENTFAILED;
          }
        }
      }
    },
    [creditCardPay.rejected]: (state) => {
      if (!state.bitPaymentOption) {
        state.currentStep = stepEnum.PAYMENTFAILEDWITHOUTBITOPTION;
      } else {
        if (state.selectedBanData.isEnergy) {
          state.currentStep = stepEnum.PAYMENTFAILEDENERGY;
        } else {
          state.currentStep = stepEnum.PAYMENTFAILED;
        }
      }
    },
    [openBitRequest.pending]: (state) => {
      state.bitReturnEvent = false;
    },
    [openBitRequest.fulfilled]: (state, action) => {
      if (action.payload?.error) {
        state.error = true;
        state.currentStep = stepEnum.BITGENERALERROR;
      }
    },
    [openBitRequest.rejected]: (state) => {
      state.currentStep = stepEnum.BITGENERALERROR;
    },
    [getBitPaymentStatus.pending]: (state) => {
      state.bitReturnEvent = false;
    },
    [getBitPaymentStatus.fulfilled]: (state, action) => {
      if (!action.payload || action.payload?.error) {
        state.error = true;
        state.currentStep = stepEnum.BITGENERALERROR;
      } else {
        const nextStep = getBitResultStep(action.payload.status, action.payload.statusCode);
        if (nextStep == stepEnum.PAYMENTSUCCESS) {
          applyBitPaymentSuccess(state, action.payload);
        }
        state.currentStep = nextStep;
      }
    },
    [getBitPaymentStatus.rejected]: (state) => {
      state.currentStep = stepEnum.BITGENERALERROR;
    },
    [getBitPaymentStatusOnLoading.fulfilled]: (state, action) => {
      if (action.payload) {
        const nextStep = getBitResultStep(action.payload.status, action.payload.statusCode);
        if (nextStep) {
          if (nextStep == stepEnum.PAYMENTSUCCESS) {
            applyBitPaymentSuccess(state, action.payload);
          }
          state.currentStep = nextStep;
        }
      }
    },
    [submitBankPaymentMethod.fulfilled]: (state, action) => {
      if (action.payload.success) {
        state.currentStep = stepEnum.UPDATEPAYMENTSUCCESS;
        state.paymentData = {...state.paymentData, ...action.payload};
      } else {
        state.currentStep = stepEnum.UPDATEPAYMENTFAILEDBANK;
      }
    },
    [submitBankPaymentMethod.rejected]: (state, action) => {
      state.currentStep = stepEnum.UPDATEPAYMENTFAILEDBANK;
    },
    [submitCCPaymentMethod.fulfilled]: (state, action) => {
      if (!action.payload.success) {
        state.currentStep = stepEnum.UPDATEPAYMENTFAILEDCC;
      } else {
        state.currentStep = stepEnum.UPDATEPAYMENTSUCCESS;
        state.paymentData = {...state.paymentData, ...action.payload};
      }
    },
    [submitCCPaymentMethod.rejected]: (state) => {
      state.currentStep = stepEnum.UPDATEPAYMENTFAILEDCC;
    },
    [sendEntityPaymentML.fulfilled]: (state, action) => {
      if (action.payload.Header && action.payload.Header.ReturnCode === 0) {
        state.currentStep = stepEnum.SENDSMSENTITYPAYMENTSUCCESS;
      } else {
        state.currentStep = stepEnum.GENERALERROR;
      }
    },
  },
});

export const {
  handleCurrentStep,
  setSelectedPaymentType,
  setSelectedUpdatePaymentType,
  handleBanData,
  setPaymentData,
  setPaymentAction,
  setSendPayment,
  endProccess,
  setBitPaymentOption,
  setPaymentFloors,
  setPaymentAmount,
  setIsJetConnection,
  setSuccessPage,
  logout,
  setEpiData,
  setShowPopup,
  setSelectedBansForPaymentUpdate,
  setBankObligationInfo,
  setSignatureFile,
  setSignatureFileSigned,
  setEntityPaymentPhoneNumber,
  saveToHistory,
  backHistory,
  clearHistory,
} = debtAndUpdatePaymentSlice.actions;

export const selectSelectedBanData = (state) => state.debtAndUpdatePayment.selectedBanData;
export const selectCurrentStep = (state) => state.debtAndUpdatePayment.currentStep;
export const selectPaymentAmount = (state) => state.debtAndUpdatePayment.paymentAmount;
export const selectGroupedBans = (state) => state.debtAndUpdatePayment.groupedBans;
export const selectPaymentMethodBans = (state) => ({
  paymentIssueBans: state.debtAndUpdatePayment.groupedBans.FINANCIAL_ISSUES.filter(
    (ban) => ban.hasPaymentMethodIssue && !ban.isEnergy && !ban.hasDebt,
  ),
  paymentIssueBansEnergy: state.debtAndUpdatePayment.groupedBans.FINANCIAL_ISSUES.filter(
    (ban) => ban.hasPaymentMethodIssue && ban.isEnergy && !ban.hasDebt,
  ),
  selectedForPaymentUpdate: state.debtAndUpdatePayment.selectedBansForPaymentUpdate,
});
export const selectFinancialIssuesBans = (state) => state.debtAndUpdatePayment.groupedBans.FINANCIAL_ISSUES;
export const selectNoFinancialIssuesBans = (state) => state.debtAndUpdatePayment.groupedBans.NO_FINANCIAL_ISSUES;
export const selectDisplayConfig = (state) => state.debtAndUpdatePayment.groupedBans.displayConfig;
// For backward compatibility - returns flattened list
export const selectServerData = (state) => {
  const grouped = state.debtAndUpdatePayment.groupedBans;
  return [...grouped.FINANCIAL_ISSUES, ...grouped.NO_FINANCIAL_ISSUES];
};
export const selectPaymentData = (state) => state.debtAndUpdatePayment.paymentData;
export const selectBitReturnEvent = (state) => state.debtAndUpdatePayment.bitReturnEvent;
export const selectSelectedPaymentType = (state) => state.debtAndUpdatePayment.selectedPaymentType;
export const selectSelectedUpdatePaymentType = (state) => state.debtAndUpdatePayment.selectedUpdatePaymentType;
export const selectBitPaymentOption = (state) => state.debtAndUpdatePayment.bitPaymentOption;
export const selectPaymentFloors = (state) => ({
  firstFloorAmount: state.debtAndUpdatePayment.firstFloorAmount,
  secondFloorAmount: state.debtAndUpdatePayment.secondFloorAmount,
  thirdFloorAmount: state.debtAndUpdatePayment.thirdFloorAmount,
  firstFloorPayments: state.debtAndUpdatePayment.firstFloorPayments,
  secondFloorPayments: state.debtAndUpdatePayment.secondFloorPayments,
  thirdFloorPayments: state.debtAndUpdatePayment.thirdFloorPayments,
  fourthFloorPayments: state.debtAndUpdatePayment.fourthFloorPayments,
});
export const selectYDaysCount = (state) => {
  return {
    yDaysCount: state.debtAndUpdatePayment.paymentStatusDisplayDaysY,
    yDaysCountEnergy: state.debtAndUpdatePayment.energyPaymentStatusDisplayDaysY,
  };
};
export const selectPaymentAction = (state) => state.debtAndUpdatePayment.paymentAction;
export const selectForm = (state) => state.debtAndUpdatePayment.form;
export const selectChosenBanId = (state) => state.debtAndUpdatePayment.chosenBanId;
export const selectIsJetConnection = (state) => state.debtAndUpdatePayment.isJetConnection;
export const selectActiveJetBan = (state) => state.debtAndUpdatePayment.activeJetBan;
export const selectMaxPayment = (state) => state.debtAndUpdatePayment.maxPayment;
export const selectMaxPaymentEnergy = (state) => state.debtAndUpdatePayment.maxPaymentEnergy;
export const selectError = (state) => state.debtAndUpdatePayment.error;
export const selectEpiData = (state) => state.debtAndUpdatePayment.epiData;
export const selectPopupData = (state) => ({
  showPopup: state.debtAndUpdatePayment.showPopUp,
  popupType: state.debtAndUpdatePayment.popupType,
  popUpProps: state.debtAndUpdatePayment.popUpProps,
});
export const selectBanksList = (state) => {
  const banksList = state.debtAndUpdatePayment.banksList;
  return Array.isArray(banksList) ? banksList : getListFromResponse(banksList);
};
export const selectBankObligationInfo = (state) => state.debtAndUpdatePayment.bankObligationInfo;
export const selectSignatureFile = (state) => state.debtAndUpdatePayment.signatureFile;
export const selectEntityPaymentPhoneNumber = (state) => state.debtAndUpdatePayment.entityPaymentPhoneNumber ?? '';
export default debtAndUpdatePaymentSlice.reducer;
